/**
 * Instant, local keyword matching over page info chunks.
 *
 * Every query word must match somewhere in the chunk. Matching is on word
 * prefixes with diacritics folded, so "lae" finds "Lae üles", "kaust" finds
 * "kaustad", and "oigus" finds "õigus". Estonian inflects heavily, so two
 * longer words also match when they share a stem: a common prefix of at least
 * STEM_MIN letters, falling short of the shorter word by at most STEM_SLACK
 * ("prügikastist" ~ "prügikast", "taastamine" ~ "taasta", but not "change" ~
 * "channel"). Title hits outrank page-name hits, which outrank body hits.
 */
import type { PageInfoChunk } from "./pageInfoChunks";

const WEIGHT = { title: 3, page: 2, body: 1 } as const;
/** Below this, a query word is too short to stand on its own. */
const MIN_WORD = 2;
const STEM_MIN = 5;
const STEM_SLACK = 3;

export function foldText(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function tokenise(text: string): string[] {
  return foldText(text)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word.length >= MIN_WORD);
}

function commonPrefix(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
}

function wordMatches(queryWord: string, word: string): boolean {
  if (word.startsWith(queryWord)) return true;
  if (queryWord.length < STEM_MIN) return false;
  const shared = commonPrefix(queryWord, word);
  return shared >= Math.max(STEM_MIN, Math.min(queryWord.length, word.length) - STEM_SLACK);
}

function fieldScore(queryWord: string, words: readonly string[]): number {
  return words.some((word) => wordMatches(queryWord, word)) ? 1 : 0;
}

interface IndexedChunk {
  chunk: PageInfoChunk;
  title: string[];
  page: string[];
  body: string[];
}

export function indexChunks(chunks: readonly PageInfoChunk[]): IndexedChunk[] {
  return chunks.map((chunk) => ({
    chunk,
    title: tokenise(chunk.title),
    page: tokenise(chunk.pageTitle),
    body: tokenise(chunk.points.join(" ")),
  }));
}

export interface KeywordHit {
  chunk: PageInfoChunk;
  score: number;
}

export function keywordSearch(index: readonly IndexedChunk[], query: string): KeywordHit[] {
  const words = tokenise(query);
  if (words.length === 0) return [];

  const hits: KeywordHit[] = [];
  for (const entry of index) {
    let score = 0;
    let allMatched = true;
    for (const word of words) {
      const best = Math.max(
        WEIGHT.title * fieldScore(word, entry.title),
        WEIGHT.page * fieldScore(word, entry.page),
        WEIGHT.body * fieldScore(word, entry.body)
      );
      if (best === 0) {
        allMatched = false;
        break;
      }
      score += best;
    }
    if (allMatched) hits.push({ chunk: entry.chunk, score });
  }
  return hits.sort((a, b) => b.score - a.score);
}
